import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { dashboardv99990101Client } from '@sequencehq/api/dashboard/v99990101'
import { Currency, useMutation, useQuery } from '@sequencehq/api/utils'
import {
  Text,
  Banner,
  Button,
  Checkbox,
  EmptyState,
  GreyGrey40,
  GreyGrey60,
  GreyGrey80,
  IconArrowOutOfBox,
  Primary13Regular,
  Primary13SemiBold,
  RedRed10,
  RedRed70,
  SettingsGroup,
  SimpleModal,
  useNotifications,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { toggleInSet } from '@sequencehq/utils'
import { MINUTES } from '@sequencehq/utils/dates'
import Spinner from 'components/Loading/Spinner'
import { useReconnectToIntegration } from 'Integrations/index'
import { inflect } from 'inflection'
import { ReactElement, cloneElement, useMemo, useState } from 'react'
import { match } from 'ts-pattern'
import { syncForMonth, useXeroSyncStatus, XeroSyncBadge } from '../xeroSync'
import { derivePushablePeriods } from './pushablePeriods'
import { useXeroIntegrationStatus } from './useXeroIntegrationStatus'
import {
  waitForXeroPushToSettle,
  type XeroPushOutcome
} from './waitForXeroPushToSettle'

/**
 * Pushes the recognition journals for the selected periods to Xero. The list of
 * pushable periods is derived in {@link derivePushablePeriods} from the Xero
 * sync-status months. The push
 * endpoint takes a single date range, so we fire one request per selected period
 * — that faithfully handles a user deselecting a month in the middle of the
 * range.
 *
 * Each period shows its current Xero sync state as a badge, and the periods never
 * pushed are preselected. The push is async, so on submit the modal closes and a
 * toast tracks progress to completion (see {@link waitForXeroPushToSettle}).
 *
 * Whether journals land in Xero as draft or posted is resolved server-side from
 * the account's Revenue Recognition settings, not chosen here — the copy points
 * users there.
 */
export const PushToXeroModal = (props: {
  trigger: ReactElement
  currency: Currency
}) => {
  const { displayNotification, displayPromiseNotification } = useNotifications()
  const [isOpen, setIsOpen] = useState(false)
  const [selectedIds, setSelectedIds] = useState<Set<string> | null>(null)
  const [pushError, setPushError] = useState<string | null>(null)

  const { isConnected, isLoading: isXeroLoading } = useXeroIntegrationStatus({
    enabled: isOpen
  })
  const { byMonthKey: xeroSyncByMonth, isLoading: isSyncLoading } =
    useXeroSyncStatus({ currency: props.currency, enabled: isOpen })
  const { reconnect, reconnecting } = useReconnectToIntegration()

  // The sync-status endpoint returns one entry per month with recognition
  // journals, so the pushable periods derive from it — no separate journals fetch.
  const periods = useMemo(
    () => derivePushablePeriods(xeroSyncByMonth.keys(), new Date()),
    [xeroSyncByMonth]
  )

  const pushMutation = useMutation(
    dashboardv99990101Client.postJournalPushToXero
  )

  // The draft/posted mode is resolved server-side from Revenue Recognition
  // settings; we read it here only to tell the user how their journals will land.
  const settingsQuery = useQuery(
    dashboard20240730Client.getRevenueRecognitionSettings,
    undefined,
    { staleTime: 1 * MINUTES, enabled: isOpen }
  )
  const journalPostMode = settingsQuery.data?.integrationsJournalPostMode

  // Preselect the periods never pushed to Xero — the ones an accountant most
  // likely wants to send. Recomputes as the sync status loads.
  const preselectedIds = useMemo(
    () =>
      new Set(
        periods
          .filter(
            period =>
              syncForMonth(xeroSyncByMonth, period.id).state === 'NOT_SYNCED'
          )
          .map(period => period.id)
      ),
    [periods, xeroSyncByMonth]
  )

  // null = follow the preselection; a Set = the user has changed the selection.
  const effectiveSelectedIds = selectedIds ?? preselectedIds
  const chosenPeriods = periods.filter(period =>
    effectiveSelectedIds.has(period.id)
  )
  const selectedCount = chosenPeriods.length

  const allSelected =
    periods.length > 0 &&
    periods.every(period => effectiveSelectedIds.has(period.id))
  const someSelected = periods.some(period =>
    effectiveSelectedIds.has(period.id)
  )

  const onToggleSelectAll = () => {
    setSelectedIds(
      allSelected ? new Set() : new Set(periods.map(period => period.id))
    )
  }

  const onClose = () => {
    setIsOpen(false)
    setSelectedIds(null)
    setPushError(null)
  }

  const onSubmit = async () => {
    setPushError(null)

    const results = await Promise.allSettled(
      chosenPeriods.map(period =>
        pushMutation.mutateAsync({
          from: period.start,
          to: period.end,
          currency: props.currency,
          includeFutureDatedJournals: false
        })
      )
    )

    const failedPeriods = chosenPeriods.filter(
      (_, index) => results[index]?.status === 'rejected'
    )

    if (failedPeriods.length > 0) {
      const failedLabels = failedPeriods.map(period => period.label).join(', ')
      setPushError(
        `Couldn't push ${failedLabels} to Xero. Please try again, or contact support if the problem persists.`
      )
      displayNotification('Failed to push to Xero', { type: 'error' })
      return false
    }

    // The push is queued (202). Close the modal and let a toast track it to
    // completion by polling the sync status of exactly the periods we pushed.
    const pushedMonthKeys = chosenPeriods.map(period => period.id)
    const count = selectedCount
    void displayPromiseNotification(
      waitForXeroPushToSettle(pushedMonthKeys, props.currency).then(outcome => {
        if (outcome.failed > 0) {
          throw outcome
        }
        return outcome
      }),
      {
        loading: `Pushing ${count} ${inflect('period', count)} to Xero…`,
        success: outcome =>
          outcome.timedOut
            ? 'Still pushing to Xero — check back shortly.'
            : `Pushed ${count} ${inflect('period', count)} to Xero`,
        error: rejected => {
          const failed = (rejected as XeroPushOutcome | undefined)?.failed
          return typeof failed === 'number' && failed > 0
            ? `${failed} ${inflect('journal', failed)} failed to push to Xero`
            : "Couldn't confirm the push to Xero"
        }
      }
    )
    return true
  }

  return (
    <>
      {cloneElement(props.trigger, { onClick: () => setIsOpen(true) })}
      <SimpleModal
        isOpen={isOpen}
        onClose={onClose}
        title="Push to Xero"
        cancelButtonText="Cancel"
        submitButtonText={
          selectedCount === 0
            ? 'Push to Xero'
            : `Push ${selectedCount} ${inflect('period', selectedCount)}`
        }
        submittingButtonText="Pushing..."
        submitDisabled={selectedCount === 0 || !isConnected}
        onSubmit={onSubmit}
        isLoading={isSyncLoading || isXeroLoading || settingsQuery.isPending}
        contentProps={{ minHeight: '320px' }}
        data-testid="revrec.modal.pushToXero"
      >
        <Flex flexDirection="column" gap="16px">
          {!isConnected && (
            <Banner
              variant="warning"
              alignIcon="center"
              data-testid="revrec.pushToXero.notConnected"
            >
              <Grid
                templateColumns="1fr auto"
                gap="12px"
                width="100%"
                alignItems="center"
              >
                Your Xero connection isn't active. Reconnect Xero before
                pushing.
                <Button
                  variant="secondary"
                  onClick={() => {
                    void reconnect('Xero')
                  }}
                  disabled={reconnecting}
                  data-testid="revrec.pushToXero.reconnect"
                >
                  {reconnecting ? (
                    <Flex gap="8px" alignItems="center">
                      <Spinner height="16px" width="16px" color={GreyGrey60} />
                      Reconnecting...
                    </Flex>
                  ) : (
                    'Reconnect'
                  )}
                </Button>
              </Grid>
            </Banner>
          )}

          {periods.length === 0 ? (
            <Box data-testid="revrec.pushToXero.empty">
              <EmptyState
                icon={IconArrowOutOfBox}
                iconSize="sm"
                title="Nothing to push"
                body="There are no periods with journals to push to Xero yet."
              />
            </Box>
          ) : (
            <>
              <Text {...Primary13SemiBold} color={GreyGrey80}>
                Select the periods to push to Xero.
              </Text>

              <SettingsGroup
                maxWidth="100%"
                headers={[
                  {
                    width: '100%',
                    label: (
                      <Flex
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                      >
                        <Flex alignItems="center" gap="12px">
                          <Checkbox
                            isChecked={allSelected}
                            isIndeterminate={someSelected && !allSelected}
                            onChange={onToggleSelectAll}
                            data-testid="revrec.pushToXero.selectAll"
                          />
                          Period
                        </Flex>
                        Status
                      </Flex>
                    )
                  }
                ]}
              >
                {periods.map((period, index) => (
                  <Flex
                    as="label"
                    key={period.id}
                    alignItems="center"
                    justifyContent="space-between"
                    height="48px"
                    cursor="pointer"
                    borderBottom={
                      index === periods.length - 1
                        ? 'none'
                        : `1px solid ${GreyGrey40}`
                    }
                  >
                    {/* The row is a <label> wrapping the checkbox, so clicking
                        anywhere on the row toggles it natively. */}
                    <Flex alignItems="center" gap="12px">
                      <Checkbox
                        isChecked={effectiveSelectedIds.has(period.id)}
                        onChange={() =>
                          setSelectedIds(
                            toggleInSet(effectiveSelectedIds, period.id)
                          )
                        }
                        data-testid={`revrec.pushToXero.period.${period.id}`}
                      />
                      <Text
                        {...Primary13SemiBold}
                        color={GreyGrey80}
                        data-testid={`revrec.pushToXero.period.${period.id}.label`}
                      >
                        {period.label}
                      </Text>
                    </Flex>
                    <XeroSyncBadge
                      sync={syncForMonth(xeroSyncByMonth, period.id)}
                    />
                  </Flex>
                ))}
              </SettingsGroup>

              {journalPostMode && (
                <Text {...Primary13Regular} color={GreyGrey60}>
                  {match(journalPostMode)
                    .with(
                      'DRAFT',
                      () =>
                        'Journals will be pushed to Xero as drafts, following your Revenue Recognition settings.'
                    )
                    .with(
                      'POSTED',
                      () =>
                        'Journals will be pushed to Xero as posted entries, following your Revenue Recognition settings.'
                    )
                    .exhaustive()}
                </Text>
              )}
            </>
          )}

          {pushError && (
            <Text
              {...Primary13Regular}
              color={RedRed70}
              background={RedRed10}
              borderRadius="8px"
              padding="10px 12px"
              data-testid="revrec.pushToXero.error"
            >
              {pushError}
            </Text>
          )}
        </Flex>
      </SimpleModal>
    </>
  )
}
